/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

//phoenix imports
import { DimensionValue, StyleProp, View, ViewStyle } from 'react-native';
import { ContentCardViewProps } from '@airasia-phoenix/react-native/widgets/ContentCardView';
import { Environment, LogErrorFn } from '../models';
import type { TFunction } from 'i18next';
import { Icon } from '@airasia-phoenix/react-native/tokens';
import type { BottomSheetRefs } from '@airasia-phoenix/react-native/components/BottomSheetView';
import { RefObject } from 'react';
import type { InputStepperProps } from '@airasia-phoenix/react-native/components/InputStepperView';
import { ScrollViewProps } from 'react-native';
import ERROR_FIELD, { ErrorField } from '../error';
import type { PendingFilterInteraction } from '../analytics/listAndFilter/listAndFilter.meta';
import type {
  MemoizedListViewProps,
  SectionListItem,
} from '@airasia-phoenix/react-native/components';
import type { SupportedCurrencies } from '@airasia-rn/ui';
import type { HotelIntegrations } from '../configs/HotelIntegrationsProvider';
// Hotels API Types
export interface Promo {
  promotionName: string;
  description: string;
  expiryDate: string;
  currency: string;
}

export interface VouchersRequest {
  currencyCode: string;
  email: string;
}

export type UseSmartFillSuggestionsParams = {
  query: string;
};

export interface Station {
  Lat: number;
  Long: number;
  Dest: string;
  PinYin: string;
  AAFlight: boolean;
  CityCode: string;
  CityName: string;
  TimeZone: string;
  AirportName: string;
  CountryCode: string;
  CountryName: string;
  StationCode: string;
  StationName: string;
  AlternativeName: string;
  StationType: string;
  Tag: string;
  Weightage: number;
}

export type StationListModel = {
  station: Station;
  isAllAirports: boolean;
};

export interface Suggestions {
  aa_id: string;
  city_id: string | null;
  coordinates: {
    lat: number;
    lon: number;
  };
  full_name: string;
  country_code: string;
  id: string;
  name: string;
  type: string;
}

export interface PopularSearchRawItem {
  location_id: string | number;
  aaid: string | null;
  rank: number;
  name: Record<string, string>;
}

export type PopularSearchesResponse = Record<
  string,
  { suggestions: PopularSearchRawItem[] }
>;

export type stationListModel = {
  place: Suggestions;
  isAllAirports: boolean;
};

type stationSearchIndexLike = { search: (query: string) => Station[] } | null;
type TranslationFn = (key: string) => string;

export interface UsestationListDataParams {
  query: string;
  stations: Suggestions[];
  searchIndex?: stationSearchIndexLike;
  recentstations: Array<{ station: Suggestions; isAllAirports: boolean }>;
  validDestinationCodes?: string[];
  t: TranslationFn;
}
export interface SearchStationsViewProps {
  onSearch?: (params: { to: Suggestions }) => void;
  onStationSelect?: (station: Suggestions) => void;
  initialStation?: Suggestions | null;
  isLoading?: boolean;
  testID?: string;
  destinationErrorField?: ErrorField;
  updateDestinationErrorField?: (errorField: ErrorField | undefined) => void;
}

export interface HotelRecentSearch {
  destination: {
    id: string;
    aa_id: string;
    name: string;
    full_name: string;
    coordinates: {
      lat: number;
      lon: number;
    };
    type: string;
    country_code: string;
    city_id: string | null;
    mainDisplayName: string;
    subDisplayName: string;
    iconDisplayName: string;
    iconName: string;
    isClickedDestinationItem: boolean;
  };
  date: {
    checkin: string; // YYYY-MM-DD
    checkout: string; // YYYY-MM-DD
  };
  rooms: Array<{
    adult: number;
    child: number;
    infant: number;
  }>;
  roomConfigs?: RoomConfig[];
}

export interface HotelSection<
  T extends MemoizedListViewProps = MemoizedListViewProps,
> extends Omit<SectionListItem, 'data'> {
  data: T[];
}

export interface HotelDestinationListItem
  extends Omit<MemoizedListViewProps, 'model'> {
  model: {
    station: HotelRecentSearch['destination'];
    isAllAirports: boolean;
  };
}

export interface SmartFillRequest {
  countryCode: string;
  query: string;
  language: string;
}

export interface PropertyNameSmartFillRequest {
  language: string;
  query: string;
  lat: number;
  lon: number;
}

export interface SmartFillResponse {
  suggestions: Suggestions[];
  success: boolean;
  message?: string;
}

export type VouchersResponse = Promo[];

export type HotelBookingConfirmationResponse = {
  hotel_id?: string;
  BookingID?: string;
  order_number?: string;
  order_id?: string;
  order_item_id?: string;
  booking_ref_id?: string;
  status?: string;
  creation_date_time?: string;
  checkin?: string;
  checkout?: string;
  pay_at_hotel?: boolean;
  buy_now_pay_later?: boolean;
  total_occupancy?: {
    adults?: number;
    children?: number;
    children_ages?: number[];
  };
  billing_contact?: Record<string, unknown>;
  rooms?: Array<Record<string, unknown>>;
  total_price?: Record<string, unknown>;
  supplier_info?: Record<string, unknown>;
  cancellation_policy?: Record<string, unknown>;
  payments?: Array<Record<string, unknown>>;
  links?: Record<string, unknown>;
  rate_amenities?: Array<Record<string, unknown>>;
  value_added_services?: Array<Record<string, unknown>>;
  refundable?: boolean;
  cancellable?: boolean;
  [key: string]: unknown;
};

export type HotelBookingPrecancellationResponse = {
  rate?: number | string;
  serverTime?: string | number | Date;
  cancellation_policy?: {
    refund_amount?: {
      currency?: string;
      value?: string | number;
      amount?: string | number;
    };
    penalties?: Array<{
      penalty_type?: string;
      start?: string;
      end?: string;
    }>;
  };
  [key: string]: unknown;
};

export type HotelCancelBookingRequest = {
  cancelHref: string;
  reason?: string;
  refund?: number | string | Record<string, unknown>;
};

export type HotelCancelBookingResponse = {
  status?: number;
  [key: string]: unknown;
};

export type HotelEmailVoucherRequest = {
  email: string;
  voucherHref: string;
};

export type HotelEmailVoucherResponse = {
  status?: number;
  [key: string]: unknown;
};

export type HotelBookingRoomContentResponse = {
  hotel_id?: string;
  default_locale_used?: boolean;
  room?: {
    id?: string;
    name?: string;
    description?: Record<string, unknown>;
    room_amenities?: Array<Record<string, unknown>>;
    views_from_room?: Array<Record<string, unknown>>;
    occupancy?: {
      total?: number;
      adults?: number;
      children?: number;
    };
    shared_bathroom?: boolean;
    gallery?: {
      images?: Array<Record<string, unknown>>;
    };
    bed_info?: Array<Record<string, unknown>>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

// Hotel Configuration Types
export type HotelConfig = {
  clientId?: string;
  jwtHash?: string;
  xorKey?: string;
  deeplink?: string;
  backHandler?: () => void;
  searchSource?: 'hotels' | 'home';
  nearestAirport?: string;
  onUIReady?: () => void;
  geoId?: string;
  gaId?: string;
  currency?: SupportedCurrencies;
};

export type HotelsProps = HotelConfig & {
  integrations?: Readonly<Partial<HotelIntegrations>>;
};

// Room Card Types
export interface RoomCardImage {
  id: string;
  url: string;
  caption?: string;
  heroImage?: boolean;
  category?: {
    id: string;
    name: string;
  };
  highResUrl?: string;
}

export interface RoomFactData {
  icon: string;
  label: string;
}

export interface RoomCardData {
  id: string;
  images: RoomCardImage[];
  urgencyLabel?: string;
  totalImages?: number;
  name: string;
  amenities: Array<{
    icon: string;
    label: string;
  }>;
  facts?: RoomFactData[];
  descriptionHtml?: string;
  bedInfo?: Array<{
    id?: string;
    description: string;
  }>;
  hotelImages?: HotelImage[];
  hotelName?: string;
  hotelLocation?: string;
  onImagePress?: () => void;
  onNextImage?: () => void;
  onPreviousImage?: () => void;
}

export interface PayableAtSiteData {
  currency?: string;
  value?: string;
  type?: string;
}

export interface RoomRateSelection {
  roomId: string;
  roomName: string;
  rateId: string;
  rateOptionId?: string | null;
  selectedBedGroupId?: string;
  selectedBedGroupToken?: string;
  bedInfo?: RoomCardData['bedInfo'];
  price: number;
  totalInclusivePrice?: {
    currency: string;
    value: string;
  };
  totalDiscountedPrice?: {
    currency: string;
    value: string;
  };
  cancelPenalties?: CancelPenaltyData[];
  isPayAtHotel?: boolean;
  isPayLater?: boolean;
  isPayNow?: boolean;
}

/** The right-aligned block under a bundle price: "or", EMI, and what the total covers. */
export interface BundlePriceBlock {
  prefixText?: string;
  installmentText?: string;
  supportingText?: string;
}

export interface RoomRateData {
  id: string;
  rawRateId?: string;
  rateOptionId?: string | null;
  price: number;
  currency: string;
  amenities: Array<{
    icon: string;
    label: string;
  }>;
  dealLabel?: string;
  dealTranslationKey?: string;
  dealTimeInSeconds?: number;
  isRecommended?: boolean;
  RecommendedText?: string;
  RecommendedIcon?: string;
  promotionLabel?: string;
  promotionIcon?: string;
  cancellationLabel?: string;
  cancellationEndDate?: string;
  mealPlanDisplayLabel?: string;
  perksLabel?: string;
  roomType?: string;
  roomTypeTranslationKey?: string | null;
  discountBadge?: string;
  discountBadgeIcon?: string;
  flyerDiscountLabel?: boolean;
  zoneCTag?: TagItem;
  zoneCDiscountValue?: string;
  originalPrice?: number;
  /** Strike-through of the whole stay, alongside `price` (the stay total). */
  totalOriginalPrice?: number;
  discountedPrice?: number;
  /**
   * "N% OFF" of the headline price against its strike-through — kept even when
   * `discountBadge` carries a custom label (SNAP's "Bundle Savings").
   */
  discountPercentBadge?: string;
  /** Present on a SNAP bundle rate: the card then prices the whole stay and shows this block. */
  bundlePriceInfo?: BundlePriceBlock;
  pricePerNightLabel?: string;
  totalPriceLabel?: string;
  totalInclusivePriceStayBreakdown?: string;
  pointsEarnedLabel?: string;
  buttonText?: string;
  onPress?: () => void;
  isButtonDisabled?: boolean;
  refundable?: boolean;
  mealPlanId?: number;
  mealPlanLabel?: string;
  refundType?: RefundType;
  cancelPenalties?: CancelPenaltyData[];
  isPayAtHotel?: boolean;
  isPayLater?: boolean;
  isPayNow?: boolean;
  payLaterDate?: string;
  payableAtSite?: PayableAtSiteData[];
  hotelChargedPropertyFee?: number;
  bedGroups?: BedGroupData[];
  selectedBedGroupId?: string;
  selectedBedGroupToken?: string;
  totalInclusivePrice?: {
    currency: string;
    value: string;
  };
  totalDiscountedPrice?: {
    currency: string;
    value: string;
  };
  selection?: RoomRateSelection;
  isDutyTravelRate?: boolean;
  isCrossSellRate?: boolean;
  isBlockedRate?: boolean;
  isMemberOnlyRate?: boolean;
  totalExclusivePrice?: string;
  isTaxIncluded?: boolean;
  isFeeIncluded?: boolean;
  isTaxAndFeeCombined?: boolean;
  availableRooms?: number;
  soldOutAlertLabel?: string;
  paymentDiscountType?: string;
  supplierDetails?: string;
  valueAddedServices?: ValueAddedService[];
  promo?: string;
}

// URL to JWT Types
type AlgorithmStep =
  | 'BASE64'
  | 'SPLIT_INTO_FOUR'
  | 'REVERSE'
  | 'INSERT_RANDOM_AT_3'
  | 'RAIL_FENCE_CIPHER'
  | 'SPLIT_INTO_THREE'
  | 'ADD_EXTRA_CHAR'
  | '3DES'
  | 'COL_TRANSPOSITION_ENCODE'
  | 'XOR_ENCRYPT'
  | 'SPLIT_INTO_FIVE';

type AlgorithmName =
  | 'ALGO_ONE'
  | 'ALGO_TWO'
  | 'ALGO_THREE'
  | 'ALGO_FOUR'
  | 'ALGO_FIVE'
  | 'ALGO_SIX';

export interface Synonym {
  name: AlgorithmName;
  value: string[];
}

export interface ApiData {
  source: string;
  id: string;
}

export interface SSRDataJson {
  synonyms: Synonym[];
  api_data: ApiData[];
}

interface SSRItem {
  source: string;
  ssrgroup: string;
  ssrlist: string[];
}

export interface SSRData {
  ssr_data: SSRItem[];
}

interface Headers {
  'Content-Type': string;
  channel_hash: string;
  [key: string]: string;
}

export interface HotelDealsRequest {
  userId: string | undefined;
  locale: string;
  location: {
    country: string | undefined;
    nearestAirport: string | undefined;
  };
  lob?: string[];
  currency: string;
  cardCount: {
    hotel: number;
  };
  debug: boolean;
  targetingCodes: {
    hotel: string;
  };
}

export interface HotelDealsCarouselItemProps extends ContentCardViewProps {
  model: HotelDealsItemAPIResponse;
}

/**
 * ? API types below -
 */
export interface OuterHotelDealsAPIResponse {
  groups: HotelDealsAPIResponse[];
}
export interface HotelDealsAPIResponse {
  title: string | null;
  items: HotelDealsItemAPIResponse[];
}

export interface HotelDealsItemAPIResponse {
  image?: string;
  deepLink?: string;
  title?: string;
  subTitle?: string;
  price: string;
  priceText?: string;
  rating?: string;
  maxRating?: string;
  discount?: string;
  reviewCount?: string;
  details?: {
    recommendationtype?: string | null;
    recommendationType?: string | null;
    currency?: string;
    strikethroughprice?: string;
    price?: string;
    productid?: string;
    checkindate?: string;
    checkoutdate?: string;
    totalpax?: string;
    destinationcategory?: string;
    destinationcountry?: string;
    destinationCountry?: string;
  };
  metaData?: {
    country?: string;
  };
  location: string;
  icon: string;
}

export interface NearestAirportCodeResponse {
  country: string;
  airport: string[];
  latlong: string;
  city: string;
}

export interface HotelCardShimmerProps {
  containerStyle?: ViewStyle;
  testID?: string;
  variant?: 'listing' | 'map';
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

export interface UrlToJWTInstance {
  getToken: (
    requestUrl: string,
    forceRefresh?: boolean,
  ) => Promise<string | undefined>;
  getAuthData: () => Promise<ApiResponse<SSRData>>;
  getJWTToken: (requestUrl: string) => string | undefined;
}

// Export internal types that might be needed by other modules
export type { AlgorithmStep, AlgorithmName, Headers, SSRItem };

export interface RoomData {
  id: string;
  room: RoomCardData;
  rates: RoomRateData[];
}

export type RoomFilterId =
  | 'BREAKFAST_INCLUDED'
  | 'HALF_BOARD'
  | 'FULL_BOARD'
  | 'ROOM_ONLY'
  | 'PAY_NOW'
  | 'PAY_AT_HOTEL'
  | 'BUY_NOW_PAY_LATER'
  | 'FREE_CANCELLATION'
  | 'SINGLE_BED'
  | 'TWIN_BED';

export type RoomFilterCategory = 'meal' | 'booking' | 'bed';

export interface RoomFilterOption {
  id: RoomFilterId;
  category: RoomFilterCategory;
  label: string;
  count: number;
  disabled: boolean;
  isSelected: boolean;
}

export type AppEnvironment = 'stg' | 'prd';

// Banner Types - Raw banner item
export interface CountryBannerItem {
  bannerUrl: string;
  startDate: string;
  endDate: string;
  redirectionUrl?: string;
}

// Response from static bucket - all countries in one response
// Each country value is a JSON string that needs to be parsed
export interface BannerApiResponse {
  banner: Record<string, string>;
}

export interface SpecialReqOption {
  id: string;
  value: string;
}

export interface SpecialReqItem {
  id: string;
  options: SpecialReqOption[];
  exclusive: boolean;
}

export interface SpecialReqApiResponse {
  special_request: SpecialReqItem[];
}

export type HotelBannerDisplayVariant = 'default' | 'listing';

// Transformed banner item for rendering
export interface HotelBannerItem {
  id: string;
  name?: string;
  url: string | number; // string for remote URLs, number for require() local assets
  height?: number;
  width?: number;
  redirectionUrl?: string;
}

export interface HotelBannerProps {
  banners: HotelBannerItem[];
  isLoading: boolean;
  testID?: string;
}

export interface HotelBannerViewProps {
  banners: HotelBannerItem[];
  testID?: string;
  /** Auto-scroll interval in milliseconds. Default is 3000ms */
  autoScrollInterval?: number;
  /** Callback when a banner is pressed. Used for tracking. */
  onBannerPress?: (item: HotelBannerItem, index: number) => void;
  /** Callback when a banner becomes visible. Used for tracking. */
  onBannerView?: (item: HotelBannerItem, index: number) => void;
  /** Callback when a remote banner image fails to load. */
  onBannerImageError?: (item: HotelBannerItem, index: number) => void;
  imageBorderRadius?: number;
  displayVariant?: HotelBannerDisplayVariant;
}

export type Voucher = {
  promotionName: string;
  description: string;
  expiryDate?: string;
};

export type MapVouchersToCardProps = {
  vouchers: Voucher[];
  claimedIds: string[];
  imageUrl: string;
  screenWidth: number;
  imageWidth: number;
  imageHeight: number;
  onCTAPress: (id: string, isAlreadyClaimed: boolean, position: number) => void;
  t: TFunction<'hotels'>;
};

export interface HotelApiResponse {
  status: number;
  body: {
    hotels?: SearchHotelItem[];
    page_number?: number;
    has_more_hotels?: boolean;
    stay_detail?: HotelSearchResponse['stay_detail'];
    aggregations?: HotelSearchResponse['aggregations'];
    filters?: HotelSearchResponse['filters'];
  };
}

export interface HotelPopularFilter {
  id: string;
  name: string;
  category: string;
}

export interface AggregationBucket {
  id?: string;
  label: string;
  doc_count: number;
}

export interface Aggregation {
  display_name: string;
  total_doc_count: number;
  buckets: AggregationBucket[];
}

export interface HotelSearchResultData {
  flyerDiscount: boolean;
  hotels: HotelData[];
  aggregations: Aggregation[];
  hasNextPage: boolean;
  popularFilters: HotelPopularFilter[];
}

export interface HotelSearchRequest {
  currency: string;
  country_code: string;
  checkin: string;
  checkout: string;
  occupancy: string;
  location_id: string;
  hotels_size?: number;
  page_number?: number;
  language?: string;
  user_selected_hotel_id?: string;
  selected_hotel_id?: string;
  guest_score?: string;
  star_rating?: string | string[];
  amenity?: string[];
  rate_amenity?: string[];
  category?: string[];
  payment_method?: string[];
  price_range?: string[];
  promotion_type?: string[];
  big_member_id?: string;
  mType?: string;
  landmark?: string;
  debug?: boolean;
  channel?: string | string[];
  asProfile?: '0' | '1';
}

export interface HotelImageLinks {
  '70px': string;
  '350px': string;
  '1000px': string;
}

export interface HotelImage {
  id: string;
  caption: string;
  hero_image: boolean;
  category: {
    id: number;
    name: string;
  };
  links: HotelImageLinks;
}

export interface LeadPriceDetail {
  currency: string;
  value: string;
}

export interface SearchHotelItem {
  id: string;
  name: string;
  is_hotel_selected: boolean;
  headline: string;
  location: {
    lat: string;
    lon: string;
  };
  star_rating: {
    rating: string;
    type: string;
  };
  guest_rating: {
    rating: string;
    count: number;
  };
  gallery?: {
    images: HotelImage[];
  };
  images?: HotelImage[];
  prices: {
    rate: {
      cross_sell_rate: boolean;
      duty_travel_rate?: boolean;
      staff_rate?: boolean;
      refundable?: boolean;
      lead_price: {
        base_rate: LeadPriceDetail;
        strike_through?: LeadPriceDetail;
      };
      total_price?: {
        inclusive?: LeadPriceDetail;
      };

      amenities: Array<{ id: number; name: string; rank: number }>;
      value_added_services?: Array<Partial<ValueAddedService>>;
      discount: {
        id: number;
        type: string;
        description: string;
      };
    };
  };
  amenities: Array<{ id: number; name: string }>;
  tags?: {
    left_view?: TagItem[];
    right_view?: TagItem[];
    label_above?: TagItem[];
    zone_a?: TagItem[];
    zone_b?: TagItem[];
    zone_c?: TagItem[];
  };
  distance_from_poi: number;
  category?: string;
  payment_method?: string[];
  source?: string;
  supplier?: string | { id?: string | number; name?: string; code?: string };
  supplier_id?: string | number;
  supplierId?: string | number;
  supplierid?: string | number;
  aaid?: string | number;
  aa_id?: string | number;
  supplier_name?: string;
  supplier_code?: string;
  debug?: {
    supplier_id?: string | number;
    supplierId?: string | number;
    supplierid?: string | number;
    aaid?: string | number;
    aa_id?: string | number;
  };
  supplier_details?:
    | string
    | (Record<string, unknown> & {
        supplier_id?: string | number;
        supplierId?: string | number;
        supplierid?: string | number;
        aaid?: string | number;
        aa_id?: string | number;
      });
}

// --- Root Response Interface ---

export interface HotelSearchResponse {
  page_number: number;
  default_locale_used: boolean;
  hotels: SearchHotelItem[];
  stay_detail: {
    checkin_date: string;
    checkout_date: string;
    guests_count: number;
    rooms_count: number;
    rooms: Array<{ guest_count: number }>;
  };
  aggregations: {
    aggregationList: {
      aggregations: Array<{
        display_name: string;
        total_doc_count: number;
        buckets: Array<{
          id?: string;
          label: string;
          doc_count: number;
        }>;
      }>;
    };
  };
  hasMoreHotels: boolean;
  flyer_discount: boolean;
  filters: {
    price_filter: { min: number; max: number }[];
    popular_filters?: HotelPopularFilter[];
  };
}

export interface PriceDetail {
  value: string;
  currency: string;
}

export interface PriceSectionProps {
  baseRate: PriceDetail;
  strikethroughPrice?: PriceDetail;
  label: string;
  discount?: string;
}
export interface AmenityBackendItem {
  id: number;
  name: string;
}

export interface TagItem {
  id: string;
  value: string;
  data: {
    number_of_rooms?: number;
    [key: string]: any;
  };
}

export interface PriceDetail {
  value: string;
  currency: string;
}

export interface AmenityBackendItem {
  id: number;
  name: string;
}

export interface TagItem {
  id: string;
  value: string;
  data: {
    number_of_rooms?: number;
    [key: string]: any;
  };
}

export interface ValueAddedService {
  id?: string;
  name: string;
  description?: string;
  person_count?: number;
  inclusions?: string;
  exclusions?: string;
  category?: string;
  offer_type?: string;
  frequency?: string;
  check_in_time?: {
    start?: string;
    end?: string;
  };
  check_out_time?: {
    start?: string;
    end?: string;
  };
}

export interface HotelData {
  id: string;
  /**
   * Extra query params the host wants on this hotel's details URL, so the
   * details page needs nothing from the listing (SNAP: its bundle ids).
   */
  detailsSearchParams?: Record<string, string>;
  name: string;
  starRating: number;
  userRating: string;
  reviewCount: number;
  location: string;
  landmark: string;
  baseRate: PriceDetail;
  /** Per-guest price. Snap price filters use this unless total-guest pricing is on. */
  perPersonBaseRate?: PriceDetail;
  strikethroughPrice?: PriceDetail;
  totalInclusivePrice?: PriceDetail;
  totalInclusivePriceStayBreakdown?: string;
  images: {
    primaryImage: string;
    secondaryImage?: string;
    tertiaryImage?: string;
  };
  amenities: AmenityBackendItem[];
  valueAddedServices?: ValueAddedService[];
  tags?: {
    left_view?: TagItem[];
    right_view?: TagItem[];
    label_above?: TagItem[];
    zone_a?: TagItem[];
    zone_b?: TagItem[];
    zone_c?: TagItem[];
  };
  discount?: string;
  rate?: {
    discount?: {
      type: string;
    };
  };
  isSoldOut?: boolean;
  isHighDemand?: boolean;
  flyerDiscountLabel?: boolean;
  isSearched?: boolean;
  isHighlighted?: boolean;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  supplierDetails?: string;
  bundlePriceInfo?: {
    tagText?: string;
    prefixText?: string;
    installmentText?: string;
    supportingText?: string;
    breakdown?: {
      flightLabel: string;
      flightRoute: string;
      flightPrice: string;
      hotelLabel: string;
      hotelName: string;
      hotelPrice: string;
      totalStandalonePrice: string;
      bundleSummary: string;
      bundleSavings: string;
      totalBundlePrice: string;
      installmentText?: string;
    };
  };
}

export interface HotelCardProps {
  isMapWeb: boolean;
  isSoldOut?: boolean;
  container: {
    onPress?: () => void;
    onMapLinkPress?: () => void;
    onClosePress?: () => void;
    highlighted?: boolean;
    hotelId?: string;
  };
  type: 'listing' | 'map';
  image: {
    sources: string[];
    isLcpImage?: boolean;
  };
  icon: {
    name: Icon;
    onPress?: () => void;
  };
  primaryText: string | React.ReactNode;
  hotelRating?: {
    value: number;
  };
  labelWithText?: {
    label: string;
    text: string;
  };
  secondaryText?: {
    text: string;
    landmark?: string;
    link?: string;
  };
  ratingRowContent: {
    userRating?: string;
    reviewCount?: number;
    starRating?: number;
  };
  tertiaryText?: {
    amenities: AmenityBackendItem[];
  };
  secondaryTag?: {
    text: string;
    icon?: any;
    showInfo?: boolean;
  };
  secondaryTags?: Array<{
    text: string;
    icon?: any;
    showInfo?: boolean;
  }>;
  valueAddedServices?: ValueAddedService[];
  debugText?: {
    text: string;
  };
  supportingText1?: {
    text: string;
    tone?: 'positive' | 'warning' | 'error';
  };
  labelFlyer?: {
    text: string;
    variant?: 'FILLED_MOVE' | 'TONED_GREEN';
  };
  labelDiscount?: {
    text: string;
    variant?: 'FILLED_MOVE' | 'TONED_GREEN';
  };
  priceText: {
    text: string;
    strikeThroughText?: string;
  };
  totalPriceText?: {
    text: string;
    supportingText: string;
  };
  bundlePriceInfo?: {
    tagText?: string;
    prefixText?: string;
    installmentText?: string;
    supportingText?: string;
    breakdown?: {
      flightLabel: string;
      flightRoute: string;
      flightPrice: string;
      hotelLabel: string;
      hotelName: string;
      hotelPrice: string;
      totalStandalonePrice: string;
      bundleSummary: string;
      bundleSavings: string;
      totalBundlePrice: string;
      installmentText?: string;
    };
  };
  supportingText2: {
    text: string;
  };
  promoPlaceholder?: {
    backgroundVariant?: 'secondary' | 'highlight' | 'warning' | 'info';
    backgroundColor?: string;
    textColor?: string;
    promoExpiryInSeconds?: number;
  };
  monetaryTag?: {
    text: string;
    variant?: 'FILLED_MOVE' | 'TONED_GREEN';
  };
  promoIconWithText?: {
    iconName?: string;
    text: string;
  };
}
export interface HotelCardViewProps {
  props: HotelCardProps;
  showDivider?: boolean;
}

export interface SmartfillResponse {
  name: string;
  type?: string;
  coordinates?: {
    lat?: number | string;
    lon?: number | string;
  };
  location?: {
    lat?: number | string;
    lon?: number | string;
  };
  lat?: number | string;
  lon?: number | string;
  nearby_poi?: Array<{
    name: string;
    id?: string;
    hotels_count?: number;
  }>;
  neighborhood?: Array<{
    name: string;
    id?: string;
    hotels_count?: number;
  }>;
}

// Static API Service Configuration
export interface StaticApiServiceConfig {
  environment?: AppEnvironment;
  baseUrl?: string;
  logError?: LogErrorFn;
}

export interface MapSavedButtonProps {
  onMapPress?: () => void;
  onSavedPress?: () => void;
}

export interface ChipViewProps {
  text: string;
  isSelected: boolean;
  disabled: boolean;
  showIcon?: boolean;
}

export interface HotelsSortViewProps {
  filterData: ChipViewProps[];
}

export interface FilterWidgetProps {
  promotionsList: { label: string; value: string }[];
  paymentOptions: { label: string; value: string }[];
  propertyTypes: { label: string; value: string }[];
  locations: { label: string; value: string }[];
  neighbourhoods: { label: string; value: string }[];
  amenities: { label: string; value: string }[];
  hotelClassData: ChipViewProps[];
  guestRatingsData: ChipViewProps[];
  popularFiltersData: ChipViewProps[];
}

export interface FilterChipSectionProps {
  title: string;
  chips: ChipViewProps[];
  onChange: (chips: ChipViewProps[]) => void;
  orientation: any;
  isMultiChoice: boolean;
  viewHeight?: DimensionValue;
  /**
   * Overrides the 2px horizontal padding Phoenix ChipGroupView puts around every
   * chip. That padding lands on both neighbours, so it adds 4px on top of the
   * orientation's horizontalSpacing — pass 0 when the spec states an exact gap.
   */
  chipPaddingHorizontal?: DimensionValue;
}
export interface BottomSheetViewProps {
  bottomSheetRef: RefObject<BottomSheetRefs | null>;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  height?: number;
  footerHeight?: number;
  stickyFooter?: React.ReactNode;
  scrollViewProps?: ScrollViewProps;
  enablePanDownToClose?: boolean;
  enableOverDrag?: boolean;
  enableHandlePanningGesture?: boolean;
  enableContentPanningGesture?: boolean;
  initialHeight?: string | number;
  finalHeight?: string | number;
  shouldAutoScroll?: React.MutableRefObject<boolean>;
}
export interface ChildGuest {
  id: string;
  age: number;
}
export interface RoomConfig {
  id: string;
  adults: number;
  children: ChildGuest[];
  infants?: number;
}
export interface GuestSelectionConfig {
  MAX_ADULTS_PER_ROOM: number;
  MIN_ADULTS_PER_ROOM: number;
  MAX_CHILDREN_PER_ROOM: number;
  MIN_CHILDREN_PER_ROOM: number;
  MAX_INFANTS_PER_ROOM?: number;
  MIN_INFANTS_PER_ROOM?: number;
  MAX_CHILD_AGE: number;
  MIN_CHILD_AGE?: number;
  DEFAULT_CHILD_AGE: number;
  WEB_MAX_HEIGHT: number;
  MAX_ROOMS: number;
  MAX_TOTAL_GUESTS?: number;
  GUEST_SHEET_HEIGHT: number;
  GUEST_SHEET_FOOTER_HEIGHT: number;
  AGE_OPTIONS: number[];
  DEFAULT_CHILD_AGE_SELECTION?: number;
}
export interface HotelGuestSelectionProps {
  rooms: RoomConfig[];
  onUpdateRooms: (rooms: RoomConfig[], isAdding?: boolean) => void;
  config: GuestSelectionConfig;
  isParentModal?: boolean;
}
export interface HotelGuestBottomSheetProps {
  bottomSheetViewRef: RefObject<BottomSheetRefs | null>;
  initialRooms: RoomConfig[];
  onConfirm: (rooms: RoomConfig[]) => void;
  isSheetOpen: boolean;
  onRequestCloseModal: () => void;
  onSheetDidClose?: () => void;
  isModalVisible?: boolean;
  anchorRef?: RefObject<View | null>;
  config: GuestSelectionConfig;
}
export interface PassengerItemProps {
  primaryText: string;
  secondaryText: string;
  inputStepperProps?: InputStepperProps;
}
export interface ChildAgePickerProps {
  bottomSheetRef?: React.RefObject<BottomSheetRefs | null>;
  onClose: () => void;
  onSelectAge: (age: number) => void;
  selectedAge?: number;
  title: string;
  isVisible?: boolean;
  onRequestClose?: () => void;
  anchorLayout?: { x: number; y: number; width: number; height: number } | null;
  config: GuestSelectionConfig;
  isParentModal?: boolean;
}

export interface RoomHeaderProps {
  index: number;
  onRemove: () => void;
  isFirst: boolean;
  isDesktopWeb?: boolean;
}

export interface AgePickerButtonProps {
  label: string;
  ageText: string;
  isFullWidth: boolean;
  isDesktopWeb: boolean;
  onPress: () => void;
  testID?: string;
  isActive?: boolean;
}

export interface CenterSheetViewWrapperProps {
  children: React.ReactNode;
  showBottomCTA?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  onReset?: () => void;
  sectionTitle: string;
}

export interface FilterButtonProps {
  onPress: () => void;
  containerStyle?: StyleProp<ViewStyle>;
  testID?: string;
  /** Overrides the badge count instead of reading it from the listing page's useFilters(). */
  activeFilterCount?: number;
}
export interface CalendarWidgetViewProps {
  checkInDate: Date | undefined;
  checkOutDate: Date | undefined;
  fromDateLabel: string;
  toDateLabel: string;
  onConfirm: (checkIn: Date, checkOut: Date | undefined) => void;
  onReset: () => void;
  onError: (message: string) => void;
  tripType?: 'round-trip' | 'one-way';
  config: CalendarConfig;
}
export interface CalendarConfig {
  maxLos: number;
  minDateOffsetDays: number;
  minReturnDateOffsetDays: number;
  maxDateOffsetDays: number;
  defaultDepartureOffsetDays: number;
  defaultReturnOffsetDays: number;
  defaultStayDays: number;
  defaultOneWayStayDays: number;
  /** Optional direct minimum date constraint (overrides minDateOffsetDays calculation) */
  minDate?: Date;
  /** Optional direct maximum date constraint (overrides maxDateOffsetDays calculation) */
  maxDate?: Date;
}

export const ACTIVE_FIELDS = {
  DEPART: 'depart',
  RETURN: 'return',
} as const;

export type ActiveField = (typeof ACTIVE_FIELDS)[keyof typeof ACTIVE_FIELDS];

export interface LfcFlightData {
  departureDate: string;
  price: number;
  shortFormattedPrice: string;
  shortPrice: string;
  airlineProfile: string;
  aaFlight: boolean;
  soldOut: boolean;
  noFlights: boolean;
}

export interface LfcResponse {
  currency: string;
  origin: string;
  destination: string;
  airlineProfiles: string[];
  cabinClass: string;
  dateRange: string;
  data: LfcFlightData[];
}
export interface AmenitiesGridItemProps {
  amenity: {
    icon: string;
    label: string;
  };
  amenityItemStyle?: object;
}

// Room Layout Types
export interface RoomLayoutProps {
  room: RoomCardData;
  displayedRates: RoomRateData[];
  roomVariant?: 'bottom-thumbnails' | 'right-thumbnails';
  testID: string;
  analyticsHotelId?: string;
  analyticsHotelName?: string;
  onDefaultImageShown?: () => void;
  styles?: {
    desktopLayout?: ViewStyle;
    desktopRoomCard?: ViewStyle;
    desktopRatesContainer?: ViewStyle;
    verticalLayout?: ViewStyle;
    ratesContainer?: ViewStyle;
  };
  showMoreText?: string;
  showLessText?: string;
  renderRatesToggle?: React.ReactNode;
  onRoomSelect?: (selection: RoomRateSelection) => void;
}

export interface RoomDesktopLayoutProps extends RoomLayoutProps {
  t: (key: string) => string;
}

/**
 * Props for mobile room layout component.
 * Extends RoomLayoutProps but omits desktop-specific props like showMoreText, showLessText, and translation function.
 * This interface provides semantic clarity for mobile-specific layout requirements.
 */
export interface RoomMobileLayoutProps
  extends Omit<RoomLayoutProps, 'showMoreText' | 'showLessText' | 't'> {}

export interface RatesToggleProps {
  testID: string;
  expandedRates: boolean;
  remainingRatesCount: number;
  onToggleRates: () => void;
  styles?: {
    toggleButton?: ViewStyle;
    toggleBtnWrapper?: ViewStyle;
  };
  t: TFunction;
}

export interface SafetyNetSelectedHotel {
  locationId: string;
  hotelId: string;
}

export interface HotelFilters {
  priceRange: [number, number];
  selectedPriceRanges: string[];
  sortBy: string;
  selectedPromos: string[];
  selectedRateAmenities?: string[];
  guestRating: string;
  selectedHotelId: string;
  selectedHotelName: string;
  starRating: number[];
  selectedAmenities: string[];
  selectedPropertyTypes: string[];
  selectedPaymentPolicies: string[];
  selectedLandmark?: string;
  isFlyerDiscountEnabled?: boolean;
  safetyNetSelectedHotel?: SafetyNetSelectedHotel | null;
}

export type FilterSectionId =
  | 'propertyName'
  | 'meals'
  | 'popular'
  | 'paymentPolicy'
  | 'price'
  | 'promotions'
  | 'hotelClass'
  | 'propertyType'
  | 'guestRating'
  | 'location'
  | 'neighbourhood'
  | 'amenities';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterPriceProps {
  currency: string;
  min: number;
  max: number;
  buckets: FilterOption[];
  showSlider: boolean;
}

export interface FilterPropertyNameProps {
  query: string;
  suggestions: Suggestions[];
  isLoading: boolean;
  onQueryChange: (query: string) => void;
}

export interface FilterWidgetViewProps {
  sections: FilterSectionId[];
  value: HotelFilters;
  onChange: (values: Partial<HotelFilters>) => void;
  aggregations?: Aggregation[];
  popularFilters?: HotelPopularFilter[];
  price?: FilterPriceProps;
  propertyName?: FilterPropertyNameProps;
  locationOptions?: FilterOption[];
  neighbourhoodOptions?: FilterOption[];
  resetKey?: number;
  /**
   * Marks the end of the scrollable filter list. Hosts use it to detect that
   * the user has scrolled the panel to the bottom — it must live inside this
   * widget's ScrollView, since a sibling sentinel never moves with the panel.
   */
  filterSectionEndRef?: (node: unknown) => void;
  /**
   * Stacks the property-name picker in a modal above a draft filter sheet.
   * Sidebar hosts leave this off and use the inline dropdown.
   */
  useModalPresentation?: boolean;
  onPromotionSelect?: (promotionName: string) => void;
  onLocationSelect?: (position: number, locationName: string) => void;
  onNeighbourhoodSelect?: (
    neighbourhoodName: string,
    neighbourhoodId: string,
  ) => void;
  onPriceRangeSelect?: (rangeLabel: string) => void;
  onTrackFilterInteraction?: (interaction: PendingFilterInteraction) => void;
}

export interface PriceRangeFilterProps {
  currency: string;
  min: number;
  max: number;
  buckets: FilterOption[];
  showSlider: boolean;
  currentValues: [number, number];
  selectedCheckboxes: string[];
  onSliderChange: (values: [number, number]) => void;
  onCheckboxChange: (values: string[]) => void;
  knobSize: number;
  resetKey?: number;
}

export interface PropertyNameFilterProps {
  query: string;
  suggestions: Suggestions[];
  isLoading: boolean;
  onQueryChange: (query: string) => void;
  useModalPresentation?: boolean;
  selectedHotelId: string;
  selectedHotelName: string;
  onSelect: (hotel: Suggestions, position: number) => void;
  onClear: () => void;
  onNoResults: (query: string) => void;
}

export interface HotelPrice {
  value: string;
}
export interface FilterOptions {
  priceRange?: [number, number];
  selectedPriceRanges: string[];
  selectedPromos: string[];
  sortBy: string;
  starRating?: number[];
}

export interface HotelListingFlags {
  hotel_banner_enabled: boolean;
  hotel_map_enabled: boolean;
  hotel_popular_filter_enabled: boolean;
  hotel_sortBy_filter_enabled: boolean;
  hotel_searchPropertyName_filter_enabled: boolean;
  hotel_paymentPolicy_filter_enabled: boolean;
  hotel_priceRange_filter_enabled: boolean;
  /**
   * Renders the "Your budget (per room per night)" slider on P30 destinations.
   * When off, P30 destinations fall back to the price bucket checkbox list, and
   * the whole price section hides if there are no buckets.
   */
  hotel_priceslider_filter_enabled: boolean;
  hotel_promotions_filter_enabled: boolean;
  hotel_hotelClass_filter_enabled: boolean;
  hotel_propertyType_filter_enabled: boolean;
  hotel_guestRating_filter_enabled: boolean;
  hotel_location_filter_enabled: boolean;
  hotel_neighbouhood_filter_enabled: boolean;
  hotel_amenities_filter_enabled: boolean;
  hotel_list_default_page_size: number;
  enable_favourite_map_marker: boolean;
  enable_filters_on_map: boolean;
  enable_saved_button: boolean;
  enable_flyer_discount_banner: boolean;
  hotel_meals_filter_enabled: boolean;
  hotel_enable_portal_usage: boolean;
}

export interface HotelSearchViewFlags {
  enable_hotel_feature_destination: boolean;
  hotel_move_list_page_enabled: boolean;
  hotel_recent_searches_enabled: boolean;
  hotel_recommendation_enabled: boolean;
  hotel_search_voucher_enabled: boolean;
  hotel_group_parameter_enabled: boolean;
  enable_hotel_search_banner: boolean;
  hotel_popular_searches_enabled: boolean;
  /** Renders check-in and check-out as one combined date field. */
  hotel_combined_date_field_enabled: boolean;
  hotel_cloudflare_image_optimization_enabled?: boolean;
}

export interface HotelDetailViewFlags {
  hotel_move_details_page_enabled: boolean;
  hotel_guest_reviews_enabled: boolean;
  hotel_shortlist_enabled: boolean;
  hotel_map_enabled: boolean;
  hotel_room_details_pre_checkout_enabled: boolean;
  hotel_room_filter_enabled: boolean;
}

export interface HotelsConfirmationFeatureFlags {
  hotels_confirmation_feature_flags: boolean;
  hotel_move_confirmation_page_enabled?: boolean;
  hotel_confirmation_cancellation_policy_enabled?: boolean;
  hotel_confirmation_nps_enabled?: boolean;
}

export interface HotelTripDetailViewFlags {
  hotel_move_tripdetails_page_enabled: boolean;
}

export interface PriceLimits {
  minLimit: number;
  maxLimit: number;
}

export interface ChinaDisclaimerModalProps {
  testID?: string;
  imageViewProps?: {
    height: number;
    width: number;
    url: string;
  };
  secondaryText: string;
  // Optional analytics props for flexible LOB support
  analyticsProps?: {
    eventName?: string;
    buildAnalyticsParams?: (
      disclaimerMessage: string,
    ) => Record<string, string | number>;
  };
  redirectRoute?: string;
}
export interface RoomConfig {
  id: string;
  adults: number;
  children: Array<{ id: string; age: number }>;
}

export interface HotelSearchParams {
  location_id: string;
  checkin: string;
  checkout: string;
  adults: string;
  rooms: string;
  children?: string;
  currency?: string;
  country_code?: string;
  countryCode?: string;
  F_R?: string;
  aaId?: string;
  user_selected_hotel_id?: string;
  guestRating: string;
  applyBigpoints?: string;
  languageId?: string;
  sort?: string;
  parsedRooms: RoomConfig[];
  occupancy: string;
  category?: string[];
  payment_method?: string[];
  star_rating?: string[];
  amenity?: string[];
  rate_amenity?: string[];
  landmark?: string;
  debug?: boolean;
  channel?: string | string[];
  mft?: string;
  source?: string;
  selected_hotel_id?: string;
}
export interface HotelSearchWidgetControls {
  onError?: (message: string) => void;
}

export type HotelSearchData = {
  destination: Suggestions | null;
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  rooms: RoomConfig[];
  destinationChanged?: boolean;
};

// Hotel Detail Response Types
export interface HotelDetailResponse {
  id: string;
  name: string;
  headline?: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    fullAddress?: string;
    country_code?: string;
  };
  description: {
    overview?: string;
    location?: string;
  };
  starRating: number;
  guestRating?: {
    rating: number;
    count: number;
    labelId?: string;
  };
  star_rating?: {
    rating: string;
    type: string;
  };
  guest_rating?: {
    rating: string;
    count: number;
    cleanliness?: string;
    service?: string;
    comfort?: string;
    condition?: string;
    location?: string;
    neighbourhood?: string;
    quality?: string;
    value?: string;
    amenities?: string;
  };
  gallery?: {
    images: Array<{
      id: string;
      hero_image: boolean;
      category: {
        id: number;
        name: string;
      };
      caption: string;
      links: {
        '70px': string;
        '350px': string;
        '1000px': string;
      };
    }>;
  };
  amenities?:
    | Array<{
        id: number;
        name: string;
      }>
    | {
        top_amenities?: Array<{ id: number; name: string }>;
        other_amenities?: Array<{ id: number; name: string }>;
      };
  checkin_details?: {
    time?: string;
    instructions?: string;
    special_instructions?: string;
  };
  checkout_details?: {
    time?: string;
  };
  policies?: {
    checkIn?: {
      from?: string;
      until?: string;
    };
    checkOut?: {
      from?: string;
      until?: string;
    };
    know_before_you_go?: string;
    age_policy?: {
      min_adult_age?: number;
      children_age_from?: number;
      children_age_to?: number;
      children_stay_free?: boolean;
      min_guest_age?: number;
    };
    childPolicies?: string;
  };
  fees?: {
    resortFee?: string;
    tax?: string;
    mandatory?: string;
    optional?: string;
  };
  nearbyPoi?: Array<{
    name?: string;
    distance?: string;
  }>;
  location?: {
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface GuestReview {
  rating?: string | number;
  rating_label?: string;
  trip_reason?: string;
  supplier?: string;
  source?: string;
  title?: string;
  text?: string;
  reviewer_name?: string;
  date_submitted?: string;
}

export interface GuestReviewsResponse {
  reviews?: GuestReview[];
}

export type GuestReviewsByHotelResponse = Record<
  string,
  GuestReviewsResponse | undefined
>;

export type GuestReviewsApiResponse =
  | GuestReviewsResponse
  | GuestReviewsByHotelResponse;

export interface ReviewCategoryScore {
  id: string;
  label: string;
  score: string;
  progress: number;
}

// Details Screen

// API Types for Hotel Rooms
export interface GetHotelRoomsParams {
  hotelId: string;
  currency?: string;
  countryCode?: string;
  checkin?: string;
  checkout?: string;
  occupancy?: string | string[] | number; // String like "2,1(7)" or array like ["2", "1(7)"], or single number for backward compatibility
  language?: string;
  fromDate?: string;
  toDate?: string;
  adults?: number;
  children?: number;
  debug?: boolean;
  channel?: string | string[];
  mft?: string;
  asProfile?: '0' | '1';
}

export interface RoomApiImage {
  id: string;
  caption?: string;
  hero_image?: boolean;
  category?: {
    id: string;
    name: string;
  };
  links: {
    '70px'?: string;
    '350px'?: string;
    '1000px'?: string;
  };
}

export interface RoomRatePrice {
  average_nightly_price?: {
    strike_through?: { currency: string; value: string };
    display_price?: { currency: string; value: string };
  };
  total_strike_through?: { currency: string; value: string };
  total_exclusive_price?: { currency: string; value: string };
  total_tax?: { currency: string; value: string };
  tax_breakdown?: Array<{
    type: string;
    name: string;
    amount: { currency: string; value: string };
  }>;
  total_fee?: { currency: string; value: string };
  fee_breakdown?: Array<{
    type?: string;
    name?: string;
    amount?: { currency: string; value: string };
  }>;
  total_inclusive_price?: { currency: string; value: string };
  total_discount?: { currency: string; value: string };
  discount_breakdown?: Array<{
    type?: string;
    name?: string;
    amount?: { currency: string; value: string };
  }>;
  total_discounted_price?: { currency: string; value: string };
}

export interface RoomRate {
  id: string;
  rate_option_id: string;
  /**
   * Ready-made savings label supplied by the caller, used in place of the
   * computed "N% OFF" badge. A flow whose saving is an amount rather than a
   * percentage — a SNAP flight + hotel bundle — formats its own wording and
   * currency in its own translation namespace and passes it through here.
   */
  savings_label?: string;
  /**
   * Ready-made "or / EMI / total price for …" block under the price, supplied
   * by a bundle flow (SNAP) that owns the wording. See `BundlePriceBlock`.
   */
  bundle_price_info?: BundlePriceBlock;
  refundable?: boolean;
  available_rooms?: number;
  airasia_exclusive?: boolean;
  pay_at_hotel_available?: boolean;
  buy_now_pay_later_available?: boolean;
  buy_now_pay_later_date?: string;
  member_only_rate?: boolean;
  duty_travel_rate?: boolean;
  member_only_rate_available?: boolean;
  cross_sell_rate?: boolean;
  blocked_rate?: boolean;
  price: RoomRatePrice;
  payable_at_site?: Array<{
    currency?: string;
    value?: string;
    type?: string;
    amount?: { currency?: string; value?: string };
  }>;
  discount?: { id?: number | string; type?: string; description?: string };
  bed_groups?: Array<{
    id: string;
    description?: string;
    token: string;
    configuration?: Array<{
      quantity: number;
      size: string;
      type: string;
    }>;
  }>;
  cancel_penalties?: Array<{
    start: string;
    end: string;
    nights?: number;
    currency?: string;
    amount?: string;
    charge_type?: string;
    percent?: string;
    penalty_type: string;
  }>;
  amenities: Array<{ id: number; name: string }>;
  promotions?: {
    deal?: {
      id: string;
      type: string;
      description: string;
    };
  };
  value_added_services?: Array<Partial<ValueAddedService>>;
  big_life?: {
    points_earn: number;
    multiplier: string;
  };
  tags?: Array<{ id: string; value: string }>;
  zone_tags?: {
    zone_a?: Array<{
      id?: string;
      value?: string;
      data?: Record<string, unknown>;
    }>;
    zone_b?: Array<{
      id?: string;
      value?: string;
      data?: Record<string, unknown>;
    }>;
    zone_c?: Array<{
      id?: string;
      value?: string;
      data?: Record<string, unknown>;
    }>;
  };
  debug_rate?: {
    supplier_id?: string | number;
    supplierId?: string | number;
    supplierid?: string | number;
    supplier_room_id?: string | number;
    supplierRoomId?: string | number;
    aaid?: string | number;
    aa_id?: string | number;
  };
}

export interface HotelPreCheckoutRequest {
  hotelId: string;
  roomId: string;
  token: string;
  language: string;
}

export interface HotelPreCheckoutResponse {
  token?: string;
  room_rate?: Partial<RoomRate> & {
    price?: RoomRatePrice;
  };
  type?: string;
  message?: string;
  field_errors?: unknown[];
  notices?: unknown[];
}

export interface HotelCheckoutFilter {
  checkin?: string;
  checkout?: string;
  occupancy?: Array<string | number>;
  languageId?: string;
  language?: string;
}

export interface HotelCheckoutPhone {
  country_code?: string;
  number?: string;
}

export interface HotelCheckoutCustomerDetails {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: HotelCheckoutPhone;
}

export interface HotelCheckoutSpecialRequestValue {
  id: string;
}

export interface HotelCheckoutSpecialRequest {
  id: string;
  value: HotelCheckoutSpecialRequestValue[];
}

export interface HotelCheckoutGuestPerRoom {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: HotelCheckoutPhone;
  country_of_passport?: string;
  special_requests_v2?: HotelCheckoutSpecialRequest[];
}

export interface HotelCheckoutSsoInfo {
  origin?: string;
  access_token?: string;
  refresh_token?: string;
  user_id?: string;
}

export type HotelCheckoutMobileDetails = Record<string, unknown> | null;

export interface HotelCheckoutRequest {
  hotel_id: string;
  room_id: string;
  rate_id: string;
  bed_group_id?: string;
  token?: string;
  customer_details: HotelCheckoutCustomerDetails;
  guests_per_room: HotelCheckoutGuestPerRoom[];
  filter: HotelCheckoutFilter;
  ga_id?: string;
  gaId?: string;
  id_key?: string;
  sso_info?: HotelCheckoutSsoInfo;
  lemon_agent_id?: string;
  cleverTapId?: string;
  customer_sso_id?: string;
  dvToken?: string;
  mobile_details?: HotelCheckoutMobileDetails;
  snowplow_id?: string;
  select_big_points?: boolean;
  buy_now_pay_later?: boolean;
  priceCheckDone?: boolean;
  fetchApi?: boolean;
}

export interface HotelCheckoutResponse {
  payment_url?: string;
  paymentUrl?: string;
  booking_request_id?: string;
  [key: string]: unknown;
}

export interface Room {
  id: string;
  name: string;
  description?: { overview?: string };
  room_amenities: Array<{ id: number; name: string }>;
  views_from_room?: Array<{ id?: string; name?: string }>;
  occupancy?: {
    total: number;
    adults?: number;
    children?: number;
  };
  shared_bathroom?: boolean;
  gallery?: {
    images: RoomApiImage[];
  };
  square_feet_area?: number;
  square_meter_area?: number;
  room_occupancy?: {
    max_occupancy: number;
    normal_beds: number;
    extra_beds: number;
  };
  bed_info?: Array<{
    id?: string;
    description: string;
    configuration?: Array<{
      quantity: number;
      size: string;
      type: string;
    }>;
  }>;
  rates?: RoomRate[];
}

// --- Rooms API Response Types ---

export interface RoomsResponse {
  hotel_id: string;
  default_locale_used?: boolean;
  debug?: {
    supplier_id?: string | number;
    supplierId?: string | number;
    supplierid?: string | number;
    aaid?: string | number;
    aa_id?: string | number;
  };
  rooms: Room[];
  stay_detail?: {
    checkin_date: string;
    checkout_date: string;
    guests_count: number;
    rooms_count: number;
    rooms: Array<{ guest_count: number }>;
  };
}

export interface AmenityData {
  id?: number;
  icon: string;
  iconKey?: string;
  label: string;
  translationKey?: string;
}

export interface BedGroupData {
  id: string;
  description?: string;
  token: string;
  configuration?: Array<{
    quantity: number;
    size: string;
    type: string;
  }>;
}

export interface CancelPenaltyData {
  start: string;
  end: string;
  nights?: number;
  currency?: string;
  amount?: string;
  chargeType?: string;
  percent?: string;
  penaltyType: string;
}

export type AmenityIconKey = string;

export interface ZoneTag {
  id?: string;
  value?: string;
  data?: Record<string, unknown>;
}

export interface ZoneTags {
  zone_a?: ZoneTag[];
  zone_b?: ZoneTag[];
  zone_c?: ZoneTag[];
}

export type RefundType =
  | 'free_cancellation'
  | 'partial_refund'
  | 'non_refundable';

export interface HotelListParams {
  location_id: string;
  checkin: string;
  checkout: string;
  occupancy: string;
  language: string;
  currency?: string;
  country_code?: string;
  hotels_size?: number;
  guest_score?: string;
  user_selected_hotel_id?: string;
  star_rating?: string | string[];
  amenity?: string[];
  rate_amenity?: string[];
  category?: string[];
  payment_method?: string[];
  price_range?: string[];
  promotion_type?: string[];
  landmark?: string;
}

// Props for single hotel mode in map view (from details page)
export interface SingleHotelMapProps {
  hotelData: HotelData;
  isDetails: boolean;
}
